// src/components/PlayerDetailDialog.jsx
// Everything a player has played in this tournament: who with, the raw score, and — behind "Show
// calculation" — the breakdown of the score each match is worth towards the ranking.
import React, { useEffect, useState } from 'react';

const names = (people) => people.map((p) => `${p.firstName} ${p.lastName}`).join(', ');

// What the match did for the player's ranking. The backend explains it in the words of the tournament's ranking
// method (`explanation`); without that, the original score's breakdown is worked out from the sets.
function Calculation({ match }) {
    const [open, setOpen] = useState(false);
    if (match.explanation?.length > 0) {
        return (
            <div className="calc">
                <button type="button" className="btn btn-ghost btn-sm calc-toggle" onClick={() => setOpen((prev) => !prev)}>
                    {open ? 'Hide' : 'Show'} calculation
                </button>
                {open && (
                    <ul className="calc-detail">
                        {match.explanation.map((line) => (
                            <li key={line}>{line}</li>
                        ))}
                    </ul>
                )}
            </div>
        );
    }
    return (
        <div className="calc">
            <button type="button" className="btn btn-ghost btn-sm calc-toggle" onClick={() => setOpen((prev) => !prev)}>
                {open ? 'Hide' : 'Show'} calculation
            </button>
            {open && (
                <ul className="calc-detail">
                    {match.sets.map((set, index) => (
                        <li key={set.setId}>
                            Set {index + 1}: {set.teamScore} of {set.teamScore + set.opponentScore} points ({(set.share * 100).toFixed(0)}%)
                            × ⅔ = {(set.share * (2 / 3)).toFixed(3)}
                            {set.setBonus > 0 && <> + ⅓ for winning the set</>}
                            {' '}→ {set.points.toFixed(3)}
                        </li>
                    ))}
                    <li>{match.matchBonus > 0 ? '+1 for winning the match' : 'No bonus for winning the match'}</li>
                    <li className="calc-total">Total for this match: {match.score.toFixed(3)}</li>
                </ul>
            )}
        </div>
    );
}

function Game({ game }) {
    return (
        <li className={`game${game.cancelled ? ' cancelled' : ''}`}>
            <div className="game-head">
                <span className="badge">Round {game.roundId + 1}</span>
                {game.isSingles && <span className="badge badge-primary">Singles</span>}
                {game.cancelled && <span className="badge badge-cancelled">Cancelled</span>}
            </div>
            <p className="game-players">
                {game.teammates.length > 0 && <>With {names(game.teammates)}. </>}
                Against {names(game.opponents)}.
            </p>
            {game.cancelled ? (
                <p className="hint">Cancelled: not counted as a game.</p>
            ) : (
                game.matches.map((match) => (
                    <div key={match.matchId} className="game-match">
                        {match.played ? (
                            <>
                                <div className="game-score">
                                    {match.sets.map((set) => (
                                        <span key={set.setId} className="game-set">
                                            {set.teamScore}–{set.opponentScore}
                                        </span>
                                    ))}
                                    <span className={`badge${match.won ? ' badge-primary' : ''}`}>
                                        {match.won === true ? 'Won' : match.won === false ? 'Lost' : 'Drawn'}
                                    </span>
                                </div>
                                <Calculation match={match} />
                            </>
                        ) : (
                            <p className="hint">Not played yet.</p>
                        )}
                    </div>
                ))
            )}
        </li>
    );
}

function PlayerDetailDialog({ player, fetchGames, onClose }) {
    const [games, setGames] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let ignore = false;
        setGames(null);
        setFailed(false);
        fetchGames(player.playerId).then((result) => {
            if (ignore) return;
            if (result) setGames(result);
            else setFailed(true);
        });
        return () => {
            ignore = true;
        };
    }, [player.playerId, fetchGames]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div
                className="modal player-detail-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="player-detail-title"
                onClick={(event) => event.stopPropagation()}
            >
                <h2 id="player-detail-title">
                    {player.firstName} {player.lastName}
                </h2>
                {games === null && !failed && <div className="skeleton" aria-busy="true" />}
                {failed && <p className="hint">Could not load this player's games.</p>}
                {games && games.length === 0 && <p className="hint">No games played yet.</p>}
                {games && games.length > 0 && (
                    <ul className="game-list">
                        {games.map((game) => (
                            <Game key={`${game.roundId}-${game.matchupId}`} game={game} />
                        ))}
                    </ul>
                )}
                <div className="modal-actions">
                    <button className="btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PlayerDetailDialog;
